import React from "react"
import RequireRoutePermit from "RequireRoutePermit"
import { Route } from "react-router-dom"
import ViewDepartment from "./okrs/department/ViewDepartment"
import ViewIndividual from "./okrs/individual/ViewIndividual"

// #region Setup
const PerformanceSetup = React.lazy(() => import("./setup/PerformanceSetup"))
const OrgChart = React.lazy(() => import("./setup/org/OrgChart"))
const Departments = React.lazy(() => import("./setup/org/Departments"))
const DepartmentMembers = React.lazy(
  () => import("./setup/org/DepartmentMembers")
)
const DepartmentManagers = React.lazy(
  () => import("./setup/org/DepartmentManagers")
)
const AddManager = React.lazy(() => import("./setup/org/AddManager"))
const UpdateMembers = React.lazy(() => import("./setup/org/UpdateMembers"))

const ChooseReviewCycle = React.lazy(
  () => import("./setup/review-cycle/ChooseReviewCycle")
)
const ReviewCycleConfig = React.lazy(
  () => import("./setup/review-cycle/ReviewCycleConfigure")
)

const RatingsConfig = React.lazy(() => import("./setup/ratings/RatingsConfig"))

const CoreValues = React.lazy(() => import("./setup/core-values/CoreValues"))
const CreateEditValues = React.lazy(
  () => import("./setup/core-values/CreateEditValues")
)
// #endregion Setup

//regon Settings
const PerformanceSettings = React.lazy(
  () => import("./settings/PerformanceSettings")
)
// #endregion Settings

// #region Review
const Review = React.lazy(() => import("./review/Review"))
const ReviewPeriod = React.lazy(
  () => import("./review/review-period/ReviewPeriod")
)
const CreateEditReviewPeriod = React.lazy(
  () => import("./review/review-period/create-edit/CreateEditReviewPeriod")
)
const ViewReviewPeriod = React.lazy(
  () => import("./review/review-period/ViewReviewPeriod")
)
const ArcDepts = React.lazy(() => import("./review/review-period/ArcDepts"))
const ViewPeerReviewers = React.lazy(
  () => import("./review/review-period/ViewPeerReviewers")
)
const ViewReviewPeriodEmployee = React.lazy(
  () =>
    import(
      "./review/review-period/review-period-employees/ViewReviewPeriodEmployee"
    )
)
const ViewReviewPeriodEmployeeReport = React.lazy(
  () =>
    import(
      "./review/review-period/review-period-employees/ViewReviewPeriodEmployeeReport"
    )
)
const ViewRevieweeReviewers = React.lazy(
  () => import("./review/review-period/ViewRevieweeReviewers")
)

const Questionnaire = React.lazy(
  () => import("./review/questionnaire/Questionnaire")
)
const CreateEditQuestionnaire = React.lazy(
  () => import("./review/questionnaire/CreateEditQuestionnaire")
)
const RegularCycleReview = React.lazy(
  () => import("./review/regular-cycle/RegularCycleReview")
)
const CreateEditRegularReviewCycle = React.lazy(
  () => import("./review/regular-cycle/CreateEditRegularReviewCycle")
)
const CreateEditOffCycle = React.lazy(
  () => import("./review/off-cycle/CreateEditOffCycle")
)

const OffCycleReview = React.lazy(
  () => import("./review/off-cycle/OffCycleReview")
)
// #endregion Review

// #region Overview
const Overview = React.lazy(() => import("./overview/Overview"))
// #region OKR
const Okr = React.lazy(() => import("./okrs/OKR"))
const ReviewPendingKR = React.lazy(() => import("./overview/ReviewPendingKR"))

const performanceRoutes = (
  <>
    {/* Setup */}
    <Route
      path="performance/setup"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <PerformanceSetup />
        </RequireRoutePermit>
      }
    />
    {
      // #region SETUP
    }
    <Route
      path="performance/setup/org/chart"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <OrgChart />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/org/departments"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <Departments />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/org/:deptId/members"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <DepartmentMembers />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/org/:deptId/managers"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <DepartmentManagers />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/org/:deptId/add-manager"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <AddManager />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/org/:deptId/update-members"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <UpdateMembers />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/review-cycle"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <ChooseReviewCycle />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/review-cycle/config"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <ReviewCycleConfig />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/ratings"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <RatingsConfig />
        </RequireRoutePermit>
      }
    />

    <Route
      path="performance/setup/core-values"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <CoreValues />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/core-values/add"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <CreateEditValues />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/setup/core-values/edit"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <CreateEditValues />
        </RequireRoutePermit>
      }
    />
    {
      // #endregion
    }

    {
      // #region Reviews
    }
    <Route
      path="performance/review"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
          <Review />
        </RequireRoutePermit>
      }
    >
      <Route
        path="review-period"
        element={
          <RequireRoutePermit allowedPermissions={["can_view_periods"]}>
            <ReviewPeriod />
          </RequireRoutePermit>
        }
      />
      <Route
        path="questionnaire"
        element={
          <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
            <Questionnaire />
          </RequireRoutePermit>
        }
      />
      <Route
        path="regular-cycle"
        element={
          <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
            <RegularCycleReview />
          </RequireRoutePermit>
        }
      />

      <Route
        path="off-cycle"
        element={
          <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
            <OffCycleReview />
          </RequireRoutePermit>
        }
      />
    </Route>
    <Route
      path="performance/review/review-period/:cdKey/:reviewPeriodKey"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_periods"]}>
          <ViewReviewPeriod />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/review/arc/:reviewPeriodKey"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_periods"]}>
          <ArcDepts />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/review/review-period/:cdKey/:reviewPeriodKey/peer-reviewers"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_periods"]}>
          <ViewPeerReviewers />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/review/review-period/:cdKey/:reviewPeriodKey/:revieweeKey/reviews"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
          <ViewReviewPeriodEmployee />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/review/review-period/:cdKey/:reviewPeriodKey/:revieweeKey/report"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
          <ViewReviewPeriodEmployeeReport />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/review/review-period/:reviewPeriodKey/reviewees/:revieweeKey"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
          <ViewRevieweeReviewers />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/review/review-period/create"
      element={
        <RequireRoutePermit allowedPermissions={["can_create_periods"]}>
          <CreateEditReviewPeriod />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/review/review-period/edit"
      element={
        <RequireRoutePermit allowedPermissions={["can_create_periods"]}>
          <CreateEditReviewPeriod />
        </RequireRoutePermit>
      }
    />
    <Route
      path="/dashboard/performance/review/regular-cycle/edit"
      element={
        <RequireRoutePermit allowedPermissions={["can_update_reviews"]}>
          <CreateEditRegularReviewCycle />
        </RequireRoutePermit>
      }
    />
    <Route
      path="/dashboard/performance/review/off-cycle/create"
      element={
        <RequireRoutePermit allowedPermissions={["can_create_reviews"]}>
          <CreateEditOffCycle />
        </RequireRoutePermit>
      }
    />
    <Route
      path="/dashboard/performance/review/off-cycle/edit"
      element={
        <RequireRoutePermit allowedPermissions={["can_update_reviews"]}>
          <CreateEditOffCycle />
        </RequireRoutePermit>
      }
    />
    <Route
      path="/dashboard/performance/review/questionnaire/create"
      element={
        <RequireRoutePermit allowedPermissions={["can_create_reviews"]}>
          <CreateEditQuestionnaire />
        </RequireRoutePermit>
      }
    />
    <Route
      path="/dashboard/performance/review/questionnaire/edit"
      element={
        <RequireRoutePermit allowedPermissions={["can_update_reviews"]}>
          <CreateEditQuestionnaire />
        </RequireRoutePermit>
      }
    />
    {
      // #endregion
    }

    {
      // #region Settings
    }
    <Route
      path="performance/settings"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <PerformanceSettings />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/settings/ratings"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <RatingsConfig isSettingsPage />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/settings/core-values"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <CoreValues isSettingsPage />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/settings/core-values/add"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <CreateEditValues isSettingsPage />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/settings/core-values/edit"
      element={
        <RequireRoutePermit
          allowedPermissions={["can_create_periods", "can_create_reviews"]}
        >
          <CreateEditValues isSettingsPage />
        </RequireRoutePermit>
      }
    />
    {
      // #endregion
    }

    <Route
      path="performance/overview"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_reviews"]}>
          <Overview />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/okr"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_objectives"]}>
          <Okr />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/okr/department"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_objectives"]}>
          <ViewDepartment showMembers={true} />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/okr/individual"
      element={
        <RequireRoutePermit allowedPermissions={["can_view_objectives"]}>
          <ViewIndividual />
        </RequireRoutePermit>
      }
    />
    <Route
      path="performance/okr/review"
      element={
        <RequireRoutePermit allowedPermissions={["can_review_key_results"]}>
          <ReviewPendingKR />
        </RequireRoutePermit>
      }
    />
  </>
)
export default performanceRoutes
